/**
 * In-memory bead-plate ("珠盤路") match history for Baccarat.
 *
 * Records the outcome of each finished round so the {@link HistoryPanel} can
 * render the classic 16×6 bead plate (up to 96 most-recent matches). Each
 * entry carries the main result plus whether either side made a pair, so the
 * panel can draw the banker-pair (red, top-left) and player-pair (blue,
 * bottom-right) dots.
 *
 * Singleton + listener pub/sub, mirroring GameLogManager. Session-only: the
 * server is authoritative for the shoe, so there's no point persisting a stale
 * client-side copy across reloads.
 */
import type { GameResult } from '../types/game';

export interface MatchHistoryEntry {
  /** Main-round winner — drives the circle colour + letter (P / B / T). */
  result: GameResult;
  /** Player's first two cards were a pair → blue dot, bottom-right. */
  playerPair: boolean;
  /** Banker's first two cards were a pair → red dot, top-left. */
  bankerPair: boolean;
}

/** 16 columns × 6 rows. */
export const MATCH_HISTORY_MAX = 96;

type Listener = () => void;

class MatchHistoryManagerImpl {
  /** Oldest first — the bead plate fills column-major from the top-left. */
  private _entries: MatchHistoryEntry[] = [];
  private listeners: Set<Listener> = new Set();

  /** Oldest first. */
  get entries(): ReadonlyArray<MatchHistoryEntry> {
    return this._entries;
  }

  get count(): number {
    return this._entries.length;
  }

  /** Per-result + per-pair tallies for the legend row. */
  get counts(): {
    total: number;
    player: number;
    banker: number;
    tie: number;
    playerPair: number;
    bankerPair: number;
  } {
    const c = { total: 0, player: 0, banker: 0, tie: 0, playerPair: 0, bankerPair: 0 };
    for (const e of this._entries) {
      c.total += 1;
      c[e.result] += 1;
      if (e.playerPair) c.playerPair += 1;
      if (e.bankerPair) c.bankerPair += 1;
    }
    return c;
  }

  add(entry: MatchHistoryEntry): void {
    this._entries.push(entry);
    // Keep only the most-recent MATCH_HISTORY_MAX (drop oldest from the front).
    if (this._entries.length > MATCH_HISTORY_MAX) {
      this._entries.splice(0, this._entries.length - MATCH_HISTORY_MAX);
    }
    this.emit();
  }

  clear(): void {
    if (this._entries.length === 0) return;
    this._entries = [];
    this.emit();
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[MatchHistoryManager listener]', e);
      }
    }
  }
}

export const MatchHistoryManager = new MatchHistoryManagerImpl();
